<template>
  <v-container fluid>
    <v-card class="elevation-11">
      <v-card-title class="accent">
        <h3>Detailed Sales Report</h3>
        <v-spacer></v-spacer>
      </v-card-title>
      <v-card-text class="pt-4">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <v-col cols="12" md="2">
              <v-switch
                v-model="allCustomers"
                label="All Customer"
                dense
                @click="clearData()"
              ></v-switch>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-autocomplete
                prepend-inner-icon="mdi-account"
                v-model="customer"
                :items="customers"
                item-text="clastname"
                item-value="custledgkey"
                label="Customer"
                persistent-hint
                return-object
                single-line
                dense
                outlined
                :disabled="allCustomers"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="formatStartDate"
                    label="Start Date"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="startDate"
                  :min="this.calcMinDate()"
                  :max="this.calcMaxDate()"
                  @input="startDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" sm="6" md="2">
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="formatEndDate"
                    label="End Date"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="endDate"
                  :min="this.calcMinDate()"
                  :max="this.calcMaxDate()"
                  @input="endDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" md="2">
              <v-btn
                color="btn-large btn-block primary white--text"
                class="mr-2"
                @click="getAgentSales()"
                :disabled="!valid"
                >Generate</v-btn
              >
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
    <v-card class="mt-5" elevation-11>
      <v-card-title>
        <v-col cols="12" md="10">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            outlined
            dense
            hide-details
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="2">
          <v-btn
            color="btn-large btn-block primary white--text"
            class="mr-2"
            @click="downloadXLS"
            :disabled="!valid"
            >XLS</v-btn
          >
          <vue-json-to-csv :json-data="agentsales" :csv-title="csvTitle">
            <v-btn color="btn-large btn-block primary white--text"> CSV </v-btn>
          </vue-json-to-csv>
        </v-col>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :loading="loading"
          :search="search"
          :headers="headers"
          :items="agentsales"
          item-key="invkey"
          class="elevation-1"
          loading-text="Retrieving data... please wait"
        >
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import AdminLayout from "@/layouts/AdminLayout";
import api from "../../services/axiosconfig";
import moment from "moment";
import VueJsonToCsv from "vue-json-to-csv";

export default {
  components: {
    VueJsonToCsv,
  },
  data() {
    return {
      valid: true,
      search: "",
      loading: false,
      allCustomers: true,
      // Widths are sized to fit the longest realistic cell value on one line.
      // `::v-deep .v-data-table__table { white-space: nowrap }` in the style
      // block below stops the browser from squeezing them back and wrapping
      // anyway; the wrapper scrolls horizontally once the total exceeds the
      // card. Roboto averages ~7px/char at the 0.75rem header and 0.875rem row
      // font sizes, plus 32px of cell padding.
      headers: [
        { text: "Month", align: "start", value: "month", width: "110px" },
        {
          text: "Booking Agent",
          align: "start",
          value: "agent",
          width: "150px",
        },
        {
          text: "Inv No.",
          align: "start",
          value: "invoicenum",
          width: "120px",
        },
        {
          text: "Inv Date",
          align: "start",
          value: "invoicedate",
          width: "150px",
        },
        { text: "PAX Name", align: "start", value: "paxname", width: "200px" },
        {
          text: "Employee Refined",
          align: "start",
          value: "accountref",
          width: "140px",
        },
        {
          text: "GTS Refined",
          align: "start",
          value: "agencyref",
          width: "120px",
        },
        {
          text: "Statement Period",
          align: "start",
          value: "period",
          width: "240px",
        },
        { text: "Airline", align: "start", value: "airline", width: "60px" },
        {
          text: "Airline Refined",
          align: "start",
          value: "airlinerefined",
          width: "60px",
        },
        {
          text: "Ticket No",
          align: "start",
          value: "ticketno",
          width: "200px",
        },
        { text: "PNR", align: "start", value: "pnrno", width: "120px" },
        {
          text: "Tkt Issue Date",
          align: "start",
          value: "ticketdate",
          width: "170px",
        },
        {
          text: "Departure Date",
          align: "start",
          value: "deptdate",
          width: "150px",
        },
        {
          text: "Return Date",
          align: "start",
          value: "retndate",
          width: "150px",
        },
        {
          text: "Flight No",
          align: "start",
          value: "flightno",
          width: "190px",
        },
        { text: "Class", align: "start", value: "class", width: "80px" },
        {
          text: "Class 1",
          align: "start",
          value: "cabintype",
          width: "120px",
        },
        { text: "DomIntl", align: "start", value: "domintl", width: "100px" },
        { text: "UDID No", align: "start", value: "", width: "80px" },
        { text: "Sector", align: "start", value: "sector", width: "250px" },
        {
          text: "O And D",
          align: "start",
          value: "orgdest",
          width: "190px",
        },
        { text: "Region", align: "start", value: "region", width: "120px" },
        { text: "Base Fare", align: "end", value: "basic", width: "110px" },
        { text: "Taxes", align: "end", value: "tax", width: "100px" },
        { text: "Service Fee", align: "end", value: "service", width: "120px" },
        { text: "Amount in LC", align: "end", value: "total", width: "130px" },
        {
          text: "Local Currency",
          align: "start",
          value: "currency",
          width: "100px",
        },
      ],

      agentsales: [],
      customers: [],
      carriers: [],
      airports: [],
      cabins: [],
      customer: {},

      startDateMenu: false,
      endDateMenu: false,

      startDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substring(0, 10),

      endDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substring(0, 10),
    };
  },

  mounted() {
    this.getAirports();
    this.getCarriers();
    this.getCabins();
    this.getCustomers();
  },

  created() {
    this.$emit(`update:layout`, AdminLayout);
  },

  computed: {
    formatStartDate() {
      return `${moment(this.startDate).format("DD-MM-YYYY")}`;
    },

    formatEndDate() {
      return `${moment(this.endDate).format("DD-MM-YYYY")}`;
    },

    formatPeriod() {
      const start = moment(this.startDate).format("DD MMM YYYY");
      const end = moment(this.endDate).format("DD MMM YYYY");
      return `${start} - ${end}`;
    },

    csvTitle() {
      return `Agent_Report_AEJ Travels-${moment(
        new Date(this.startDate)
      ).format("YYYYMMDD")}`;
    },
  },

  methods: {
    clearData() {
      this.agentsales = [];
      this.customer = {};
    },

    calcMinDate() {
      return moment()
        .subtract(localStorage.getItem("PERMITTED"), "months")
        .startOf("month")
        .format("YYYY-MM-DD");
    },

    calcMaxDate() {
      return moment().endOf("month").format("YYYY-MM-DD");
    },

    validateData() {
      if (moment(this.startDate).isAfter(this.endDate)) {
        this.$toasted.global.optimus_error({
          message: "Start date must be on or before the end date.",
        });
        return false;
      } else if (
        !this.allCustomers &&
        Object.keys(this.customer).length === 0
      ) {
        this.$toasted.global.optimus_error({
          message: "Select a customer from the drop down list.",
        });
        return false;
      } else {
        return true;
      }
    },

    dateFormat(dateString) {
      return moment(new Date(dateString)).format("MM/DD/YYYY");
    },

    monthFormat(dateString) {
      return moment(new Date(dateString)).format("MMMM");
    },

    async getAgentSales() {
      if (this.validateData()) {
        this.agentsales = [];
        try {
          let response = "";
          this.loading = true;
          if (!this.allCustomers) {
            response = await api.post("/aejmis/api/sales", {
              startdate: this.startDate,
              enddate: this.endDate,
              ledgerkey: this.customer.custledgkey,
            });
          } else {
            response = await api.post("/aejmis/api/allsales", {
              startdate: this.startDate,
              enddate: this.endDate,
            });
          }
          this.generateAgentSales(response.data);
          this.loading = false;
        } catch (error) {
          this.loading = false;
          this.$toasted.global.optimus_error({
            message: "Could not retrieve information. Try after sometime.",
          });
        }
      }
    },

    generateAgentSales(rawData) {
      this.agentsales = [];
      rawData.map(
        ({
          invoicenum,
          invoicedate,
          firstname,
          lastname,
          depdate,
          locator,
          finaldest,
          accountref,
          agencyref,
          costing,
          routing,
        }) => {
          this.agentsales.push({
            month: this.monthFormat(invoicedate),
            agent: "AEJ Travels",
            invoicenum: invoicenum,
            invoicedate: moment(new Date(invoicedate)).format("MM/DD/YYYY"),
            paxname: `${firstname.trim()} ${lastname.trim()}`,
            accountref: accountref,
            agencyref: agencyref,
            period: this.formatPeriod,
            airline: "",
            airlinerefined: this.getAirline(routing),
            ticketno: this.getTicketNum(routing, costing),
            pnrno: locator,
            ticketdate: this.getTicketDate(costing),
            deptdate: this.dateFormat(depdate),
            retndate: this.getReturnDate(routing),
            flightno: this.getFlightNum(routing),
            class: this.getCabin(routing),
            cabintype: this.getCabinType(routing),
            domintl: "INTL",
            uuidno: "",
            sector: this.encodeSectors(routing),
            orgdest: this.getORDN(routing, finaldest),
            region: "UK/EUROPE",
            basic: this.calcBasic(costing),
            tax: this.calcTax(costing),
            service: this.calcService(costing),
            total: this.calcInvTotal(costing),
            currency: "GBP",
            convrate: "",
            amtininr: "",
            avgfare: "",
            comparison: "",
            mn: "",
            remark: "",
            consider: "",
            authcode: "",
            costcenter: "",
            customer: "",
            newsmu: "",
            mainclass: "",
            newsectorpractice: "",
            itnonit: "",
            region2: "",
            traveltype: "",
            remarks: "",
            owretfinal: "",
            destinationforgeo: "",
            geo: "",
            destinationsector: "",
            sectortickets: "",
            originsector: "",
            odsector: "",
            origincountry: "",
            destcountry: "",
            odcountry: "",
            nooftickets: "",
            classfinalone: "",
            ftrbtrgl: "",
            planrate: "",
            usdplanrate: "",
            billability: "",
            smugblnew: "",
            mainclassificationnew: "",
            sectorpracticefunction: "",
            passengerticketname: "",
            newsmugblnew: "",
            newmainclassificationnew: "",
            newsectorpracticenew: "",
          });
        }
      );
    },

    getAirline(routes) {
      let first = true;
      let carrier = "";
      let sortedRoutes = routes.sort(this.customCompare);
      sortedRoutes.forEach((route) => {
        if (route.airline.length > 0 && first) {
          carrier = route.airline;
          first = false;
        }
      });
      return carrier;
    },

    getTicketNum(routes, costing) {
      let first = true;
      let prefix = "";
      let account = "";
      let sortedRoutes = routes.sort(this.customCompare);
      sortedRoutes.forEach((route) => {
        if (route.airline.length > 0 && first) {
          account = this.carriers.filter(
            (airline) => airline.iata === route.airline
          );
          first = false;
        }
      });

      if (account.length) {
        prefix = account[0].accounting;
      } else {
        prefix = "NIL";
      }

      let ticketno = "";
      let tktFirst = true;
      costing.forEach((costItem) => {
        if (tktFirst) {
          costItem.ticket.forEach((ticketItem) => {
            ticketno = ticketItem.ticketnum;
          });
          tktFirst = false;
        }
      });
      return `${prefix}-${ticketno}`;
    },

    getTicketDate(costing) {
      let ticketdt = "";
      let tdtFirst = true;
      costing.forEach((costItem) => {
        if (tdtFirst) {
          costItem.ticket.forEach((ticketItem) => {
            ticketdt = ticketItem.issuedate;
          });
          tdtFirst = false;
        }
      });
      return this.dateFormat(ticketdt);
    },

    getReturnDate(routes) {
      let rtndt = "";
      let sortedRoutes = routes.sort(this.customCompare);
      sortedRoutes.forEach((route) => {
        if (route.departuredate > "2000-01-01") {
          rtndt = moment(route.departuredate).format("MM/DD/YYYY");
        }
      });
      return rtndt;
    },

    getFlightNum(routes) {
      let flightno = "";
      let sortedRoutes = routes.sort(this.customCompare);
      sortedRoutes.forEach((route) => {
        if (route.flight.length > 0) {
          flightno =
            flightno + route.airline.trim() + route.flight.trim() + "-";
        }
      });
      flightno = flightno.slice(0, -1);
      return flightno;
    },

    calcBasic(costings) {
      let basic = 0;
      costings.forEach((item) => {
        let tmpDesc = item.description.toUpperCase().trim();
        let isService =
          tmpDesc.includes("TRANSACTION") || tmpDesc.includes("SERVICE");
        if (!isService) {
          basic = basic + item.quantity * (item.gross - item.tax);
        }
      });
      return parseFloat(basic).toFixed(2);
    },

    calcTax(costings) {
      let tax = 0;
      costings.forEach((item) => {
        tax = tax + item.quantity * item.tax;
      });
      return parseFloat(tax).toFixed(2);
    },

    calcService(costings) {
      let service = 0;
      costings.forEach((item) => {
        if (
          item.description.trim().toUpperCase() ===
            "TRANSACTION / SERVICE FEE" ||
          item.description.trim().toUpperCase() ===
            "TRANSACTION / SERVICE CHARGES"
        ) {
          service = service + item.quantity * item.priceeach;
        }
      });
      return parseFloat(service).toFixed(2);
    },

    calcInvTotal(costings) {
      let amount = 0;
      costings.forEach((item) => {
        amount = amount + item.quantity * item.gross;
      });
      return parseFloat(amount).toFixed(2);
    },

    getOWRTN(routes) {
      let first = true;
      let firstSector = "";
      let lastSector = "";
      let sortedRoutes = routes.sort(this.customCompare);
      sortedRoutes.forEach((route) => {
        if (route.airline.length > 0) {
          if (first) {
            firstSector = route.from;
            lastSector = route.destination;
            first = false;
          } else {
            lastSector = route.destination;
          }
        }
      });
      return firstSector === lastSector ? "RT" : "OW";
    },

    getORDN(routes, finaldest) {
      let first = true;
      let firstsector = "";
      let lastsector = finaldest;
      let found = false;

      this.airports.forEach((airport) => {
        if (!found) {
          if (airport.cityname == finaldest) {
            lastsector = airport.citycode;
            found = true;
          }
        }
      });
      let sortedRoutes = routes.sort(this.customCompare);
      sortedRoutes.forEach((route) => {
        if (route.airline.length > 0) {
          if (route.source === null || route.target === null) {
            if (first) {
              firstsector = route.from;
              first = false;
            }
          } else if (route.source != null && route.target != null) {
            if (first) {
              firstsector = route.source.citycode;
              first = false;
            }
          }
        }
      });
      return `${firstsector}-${lastsector}`;
    },

    encodeSectors(routes) {
      let first = true;
      let sector = "";
      let sortedRoutes = routes.sort(this.customCompare);
      sortedRoutes.forEach((route) => {
        if (route.airline.length > 0) {
          if (route.source === null || route.target === null) {
            if (first) {
              sector = route.from + "-" + route.destination;
              first = false;
            } else {
              sector = sector + "-" + route.destination;
            }
          } else if (route.source != null && route.target != null) {
            if (first) {
              sector = route.source.citycode + "-" + route.target.citycode;
              first = false;
            } else {
              sector = sector + "-" + route.target.citycode;
            }
          }
        }
      });
      return sector;
    },

    // Sort routing key
    customCompare(a, b) {
      return a.routingkey - b.routingkey;
    },

    getCabin(routes) {
      let first = true;
      let cabin = "";
      let sortedRoutes = routes.sort(this.customCompare);
      sortedRoutes.forEach((route) => {
        if (route.class.length > 0 && first) {
          cabin = route.class;
          first = false;
        }
      });
      return cabin;
    },

    getCabinType(routes) {
      let firstCarrier = true;
      let carrier = "";
      let sortedRoutes = routes.sort(this.customCompare);
      sortedRoutes.forEach((route) => {
        if (route.airline.length > 0 && firstCarrier) {
          carrier = route.airline;
          firstCarrier = false;
        }
      });

      const cabins = routes.filter((route) => route.airline === carrier);
      let firstCabin = true;
      let cabinClass = "";
      cabins.forEach((cabin) => {
        if (cabin.class.length > 0 && firstCabin) {
          cabinClass = cabin.class;
          firstCabin = false;
        }
      });

      const cabinDecoded = this.cabins.filter(
        (cabin) => cabin.cabincode === cabinClass.toUpperCase()
      );
      if (cabinDecoded.length > 0) {
        return cabinDecoded[0].cabinname;
      } else {
        return "";
      }
    },

    async getCustomers() {
      try {
        this.loading = true;
        const response = await api.get("/aejmis/api/customers");
        this.customers = response.data;
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.$toasted.global.optimus_error({
          message:
            "Could not retrieve customer information. Try after sometime.",
        });
      }
    },

    async getCarriers() {
      try {
        this.airlines = [];
        this.loading = true;
        const response = await api.get("/aejmis/api/airlines");
        this.carriers = response.data;
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.$toasted.global.optimus_error({
          message: "Could not airlines information. Try after sometime.",
        });
      }
    },

    async getCabins() {
      try {
        this.cabins = [];
        this.loading = true;
        const response = await api.get("/aejmis/api/cabins");
        this.cabins = response.data;
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.$toasted.global.optimus_error({
          message: "Could not retrieve cabin information. Try after sometime.",
        });
      }
    },

    async getAirports() {
      try {
        this.loading = true;
        const response = await api.get("/aejmis/api/airports");
        const data = response.data;
        data.map((airport) => {
          if (airport.cityname != null) {
            this.airports.push({
              citycode: airport.citycode.trim(),
              cityname: airport.cityname.trim(),
            });
          }
        });
        this.loading = false;
        return;
      } catch (error) {
        this.loading = false;
        this.$toasted.global.optimus_error({
          message: "Could not retrieve information. Try after sometime.",
        });
      }
    },

    async downloadXLS() {
      const ExcelJS = require("exceljs");
      const fileSaver = require("file-saver");

      // Create workbook & add worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Sheet");

      // add column headers
      worksheet.columns = [
        { header: "Month", key: "month", width: 12 }, // 0
        { header: "Booking Agent", key: "agent", width: 12 }, // 1
        { header: "Invoice No.", key: "invoicenum", width: 10 }, // 2
        { header: "Invoice Date", key: "invoicedate", width: 15 }, // 3
        { header: "Pax Name", key: "paxname", width: 50 }, // 4
        { header: "Employee Refined", key: "accountref", width: 20 }, // 5
        { header: "GTS Refined", key: "agencyref", width: 30 }, // 6
        { header: "Statement Period", key: "period", width: 25 }, // 7
        { header: "Airline", key: "airline", width: 10 }, // 8
        { header: "Airline Refined", key: "airlinerefined", width: 10 }, // 9
        { header: "Ticket No.", key: "ticketno", width: 20 }, // 10
        { header: "PNR", key: "pnrno", width: 15 }, // 11
        { header: "Tkt Issue Date", key: "ticketdate", width: 20 }, // 12
        { header: "Departure Date", key: "deptdate", width: 15 }, // 13
        { header: "Return Date", key: "retndate", width: 15 }, // 14
        { header: "Flight No.", key: "flightno", width: 15 }, // 15
        { header: "Class", key: "class", width: 10 }, // 16
        { header: "Class 1", key: "cabintype", width: 20 }, // 17
        { header: "DomIntl", key: "domintl", width: 10 }, // 18
        { header: "UUID No", key: "uuidno", width: 10 }, // 18
        { header: "Sector", key: "sector", width: 50 }, // 19
        { header: "O And D", key: "orgdest", width: 35 }, // 20
        { header: "Region", key: "region", width: 20 }, // 21
        {
          header: "Base Fare",
          key: "basic",
          width: 15,
          style: { numFmt: "#,##0.00" },
        }, // 22
        {
          header: "Tax",
          key: "tax",
          width: 15,
          style: { numFmt: "#,##0.00" },
        }, // 23
        {
          header: "Service Fee",
          key: "service",
          width: 15,
          style: { numFmt: "#,##0.00" },
        }, // 24
        {
          header: "Amount in LC",
          key: "total",
          width: 15,
          style: { numFmt: "#,##0.00" },
        }, // 25
        { header: "Currency", key: "currency", width: 10 }, // 26
        { header: "Conv Rate", key: "", width: 10 }, // 27
        { header: "Amount in INR", key: "", width: 10 }, // 28
        { header: "Avg Fare", key: "", width: 10 }, // 29
        { header: "Comparison", key: "", width: 10 }, // 30
        { header: "Mn", key: "", width: 10 }, // 31
        { header: "Remark", key: "", width: 10 }, // 32
        { header: "Consider", key: "", width: 10 }, // 33
        { header: "Authentication Code", key: "", width: 10 }, // 34
        { header: "Cost Center", key: "", width: 10 }, // 35
        { header: "Customer", key: "", width: 10 }, // 36
        { header: "New SMU", key: "", width: 10 }, // 37
        { header: "Main Class", key: "", width: 10 }, // 38
        { header: "New Sector Practice", key: "", width: 10 }, // 39
        { header: "IT NON IT", key: "", width: 10 }, // 40
        { header: "Region 2", key: "", width: 10 }, // 41
        { header: "Travel Type", key: "", width: 10 }, // 42
        { header: "Remarks", key: "", width: 10 }, // 43
        { header: "OW Ret Final", key: "", width: 10 }, // 44
        { header: "Destination For Geo", key: "", width: 10 }, // 45
        { header: "Geo", key: "", width: 10 }, // 46
        { header: "Destination Sector", key: "", width: 10 }, // 47
        { header: "Sector Tickets", key: "", width: 10 }, // 48
        { header: "Origin Sector", key: "", width: 10 }, // 49
        { header: "OD Sector", key: "", width: 10 }, // 50
        { header: "Origin Country", key: "", width: 10 }, // 51
        { header: "Dest Country", key: "", width: 10 }, // 52
        { header: "OD Country", key: "", width: 10 }, // 53
        { header: "No of Tickets", key: "", width: 10 }, // 54
        { header: "Class Final One", key: "", width: 10 }, // 55
        { header: "FTR BTR GL", key: "", width: 10 }, // 56
        { header: "Plan Rate", key: "", width: 10 }, // 57
        { header: "USD At Plan Rate", key: "", width: 10 }, // 57
        { header: "Billability", key: "", width: 10 }, // 58
        { header: "SMU GBL New", key: "", width: 10 }, // 59
        { header: "Main Classification New", key: "", width: 10 }, // 60
        { header: "Sector Practice Function", key: "", width: 10 }, // 61
        { header: "Passenger Ticket Name", key: "", width: 10 }, // 62
        { header: "New SMU GBL New", key: "", width: 10 }, // 63
        { header: "New Main Classification New", key: "", width: 10 }, // 64
        { header: "New Sector Practice Function", key: "", width: 10 }, // 65
      ];

      // make header bold
      worksheet.getRow(1).font = { name: "Roboto", bold: false };

      // style columns
      worksheet.columns[0].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[1].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[2].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[3].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[4].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[5].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[6].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[7].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[8].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[9].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[10].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[11].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[12].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[13].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[14].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[15].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[16].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[17].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[18].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[19].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[20].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[21].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[22].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[23].alignment = {
        vertical: "middle",
        horizontal: "right",
      };
      worksheet.columns[24].alignment = {
        vertical: "middle",
        horizontal: "right",
      };
      worksheet.columns[25].alignment = {
        vertical: "middle",
        horizontal: "right",
      };
      worksheet.columns[26].alignment = {
        vertical: "middle",
        horizontal: "right",
      };
      worksheet.columns[27].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[28].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[29].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[30].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[31].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[32].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[33].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[34].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[35].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[36].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[37].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[38].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[39].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[40].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[41].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[42].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[51].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[52].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[53].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[54].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[55].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[43].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[44].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[45].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[46].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[47].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[48].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[49].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[50].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[51].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[52].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[53].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[54].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[55].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[56].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[57].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[58].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[59].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[60].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[61].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[62].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[63].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[64].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[65].alignment = {
        vertical: "middle",
        horizontal: "left",
      };

      // Add rows using both the above of rows
      if (this.agentsales.length > 0) {
        let rows = [];
        this.agentsales.forEach((sale) => {
          rows.push({
            month: sale.month,
            agent: "AEJ Travels",
            invoicenum: sale.invoicenum,
            invoicedate: sale.invoicedate,
            paxname: sale.paxname,
            accountref: sale.accountref,
            agencyref: sale.agencyref,
            period: sale.period,
            airline: sale.airline,
            airlinerefined: sale.airlinerefined,
            ticketno: sale.ticketno,
            pnrno: sale.pnrno,
            ticketdate: sale.ticketdate,
            deptdate: sale.deptdate,
            retndate: sale.retndate,
            flightno: sale.flightno,
            class: sale.class,
            cabintype: sale.cabintype,
            domintl: sale.domintl,
            owrt: sale.owrt,
            sector: sale.sector,
            orgdest: sale.orgdest,
            region: sale.region,
            basic: parseFloat(sale.basic) || 0,
            tax: parseFloat(sale.tax) || 0,
            service: parseFloat(sale.service) || 0,
            total: parseFloat(sale.total) || 0,
            currency: sale.currency,
            convrate: "",
            amtininr: "",
            avgfare: "",
            comparison: "",
            mn: "",
            remark: "",
            consider: "",
            authcode: "",
            costcenter: "",
            customer: "",
            newsmu: "",
            mainclass: "",
            newsectorpractice: "",
            itnonit: "",
            region2: "",
            traveltype: "",
            remarks: "",
            owretfinal: "",
            destinationforgeo: "",
            geo: "",
            destinationsector: "",
            sectortickets: "",
            originsector: "",
            odsector: "",
            origincountry: "",
            destcountry: "",
            odcountry: "",
            nooftickets: "",
            classfinalone: "",
            ftrbtrgl: "",
            planrate: "",
            usdplanrate: "",
            billability: "",
            smugblnew: "",
            mainclassificationnew: "",
            sectorpracticefunction: "",
            passengerticketname: "",
            newsmugblnew: "",
            newmainclassificationnew: "",
            newsectorpracticenew: "",
          });
        });

        worksheet.addRows(rows);

        const firstDataRow = 2;
        const lastDataRow = 1 + rows.length;

        const basicCol = worksheet.getColumn("basic").letter;
        const taxCol = worksheet.getColumn("tax").letter;
        const serviceCol = worksheet.getColumn("service").letter;
        const totalCol = worksheet.getColumn("total").letter;

        let totalBasic = 0;
        let totalTax = 0;
        let totalService = 0;
        let totalTotal = 0;

        this.agentsales.forEach((sale) => {
          totalBasic += parseFloat(sale.basic) || 0;
          totalTax += parseFloat(sale.tax) || 0;
          totalService += parseFloat(sale.service) || 0;
          totalTotal += parseFloat(sale.total) || 0;
        });

        const totalRow = worksheet.addRow({
          month: "Total",
          basic: {
            formula: `SUM(${basicCol}${firstDataRow}:${basicCol}${lastDataRow})`,
            result: parseFloat(totalBasic.toFixed(2)),
          },
          tax: {
            formula: `SUM(${taxCol}${firstDataRow}:${taxCol}${lastDataRow})`,
            result: parseFloat(totalTax.toFixed(2)),
          },
          service: {
            formula: `SUM(${serviceCol}${firstDataRow}:${serviceCol}${lastDataRow})`,
            result: parseFloat(totalService.toFixed(2)),
          },
          total: {
            formula: `SUM(${totalCol}${firstDataRow}:${totalCol}${lastDataRow})`,
            result: parseFloat(totalTotal.toFixed(2)),
          },
        });

        totalRow.font = { name: "Roboto", bold: true };

        workbook.xlsx.writeBuffer().then((data) => {
          const blob = new Blob([data], { type: "application/octet-stream" });
          fileSaver.saveAs(
            blob,
            `Agent_Report_AEJ Travels-${moment(new Date(this.startDate)).format(
              "YYYYMMDD"
            )}.xlsx`
          );
        });
      }
    },
  },
};
</script>

<style scoped>
/* Vuetify sets `.v-data-table__wrapper > table { width: 100% }`, so the
   browser compresses these 28 columns below the widths declared in `headers`
   and wraps the text. `white-space: nowrap` raises each column's minimum
   width to its full content width instead, letting the table grow past the
   card; the wrapper already has `overflow-x: auto`, so it scrolls sideways.

   `width: auto` is required for the truncation below: browsers ignore
   `max-width` on a `td` while the table is stretched to `100%`. With `auto`
   the table still sizes to its nowrap content, so scrolling is unchanged. */
::v-deep .v-data-table__table {
  white-space: nowrap;
  width: auto;
}

/* `text-overflow` has no effect without `overflow: hidden`. Harmless on cells
   whose content already fits their column. */
::v-deep .v-data-table__table td {
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Columns whose values vary in length truncate with an ellipsis instead of
   stretching the column. `nth-child` is 1-based over the `headers` array;
   each cap matches that column's declared width, so typical values are
   unaffected and only unusually long ones are clipped. */
::v-deep .v-data-table__table td:nth-child(5),
::v-deep .v-data-table__table td:nth-child(11) {
  max-width: 200px;
  /* PAX Name, Ticket Number */
}

::v-deep .v-data-table__table td:nth-child(6),
::v-deep .v-data-table__table td:nth-child(7),
::v-deep .v-data-table__table td:nth-child(18) {
  max-width: 130px;
  /* Employee No., GTS/TR No., Class Type */
}

::v-deep .v-data-table__table td:nth-child(16),
::v-deep .v-data-table__table td:nth-child(22) {
  max-width: 190px;
  /* Flight Number, Origin-Destination */
}

::v-deep .v-data-table__table td:nth-child(21) {
  max-width: 250px;
  /* Sector */
}
</style>
